/* eslint-disable */
import { Button, Container, Icon, Row } from '@zoom/components';
import { TFunction } from 'i18next';
import cloneDeep from 'lodash.clonedeep';
import { equals, pathOr } from 'ramda';
import React, { Component, useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router';
import { isValid, startOfDay } from 'date-fns';

import { Permissions } from '../../app/AuthorizationProvider';
import { BackgroundTasksStatusConsumer, isScheduleJobRunning } from '../../app/BackgroundTasksStatusProvider';
import { ActiveFilters } from '../../components/ActiveFilters/ActiveFilters';
import { FullSizeMessage } from '../../components/FullSizeMessage';
import { ListFilter, ListFilterResults } from '../../components/ListFilter';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { ModalConfirmation } from '../../components/ModalConfirmation';
import { Content, HeaderPropsType } from '../../components/layout/Content';
import { SidebarConsumer } from '../../components/layout/Sidebar';
import {
  GetSchedulesComponent,
  GetSchedulesQueryVariables,
  DeleteScheduleMutationFn,
  GetSchedulesDocument,
  DeleteScheduleComponent,
  LockType,
  Queue,
} from '../../graphql/components';
import { PATH_SCHEDULING_STUDIO_CREATE } from '../../router/routes';
import { compose } from '../../utils/compose';
import { createEndOfDateRange } from '../../utils/dateRange';
import { ScheduleList, ScheduleType } from './ScheduleList';
import { SchedulingStudioFilterSidebar } from './SchedulingStudioFilterSidebar';
import { STUDIO_TITLE_TAG, TOGGLE_FILTER_BUTTON_TAG } from './constants';
import { QueuesResult, SchedulesResult } from '../../graphql/types';
import { recordBy } from '../../utils/array';

export const defaultGetSchedulesVariables: GetSchedulesQueryVariables = {
  queueIds: undefined,
  scheduleEndDateTime: '',
  scheduleName: '',
  scheduleStartDateTime: '',
};

const defaultState: SchedulingStudioSceneState = {
  getSchedulesVariables: defaultGetSchedulesVariables,
  isDeleteModalOpen: false,
  mutationInProgress: false,
  preOpenedFilterItemIds: [],
  selectedSchedule: undefined,
};

interface SchedulingStudioSceneState {
  getSchedulesVariables: GetSchedulesQueryVariables;
  isDeleteModalOpen: boolean;
  mutationInProgress: boolean;
  preOpenedFilterItemIds: string[];
  selectedSchedule?: ScheduleType;
  preFilteredDate?: Date;
}

type SchedulingStudioSceneProps = WithTranslation &
  RouteComponentProps &
  GetSchedulesQueryVariables & {
    deleteSchedule: DeleteScheduleMutationFn;
  };

class SchedulingStudioSceneComponent extends Component<SchedulingStudioSceneProps, SchedulingStudioSceneState> {
  public constructor(props: SchedulingStudioSceneProps) {
    super(props);

    /**
     * This code is extracting query parameters from the URL and setting the state of the component accordingly
     *  for pre-opening filters when leading from Intraday scene - go to Schedule button.
     */
    const {
      location: { search },
      history,
    } = this.props;

    if (search) {
      const queryParameters = new URLSearchParams(search);

      const queueIdQueryParam = queryParameters.get('queueId');
      const dateQueryParam = queryParameters.get('date');

      if (queueIdQueryParam && dateQueryParam) {
        const parsedDate = new Date(dateQueryParam);

        if (isValid(parsedDate)) {
          this.state = {
            ...defaultState,
            preFilteredDate: parsedDate,
            getSchedulesVariables: {
              ...defaultGetSchedulesVariables,
              queueIds: queueIdQueryParam,
              scheduleEndDateTime: createEndOfDateRange(parsedDate).toISOString(),
              scheduleStartDateTime: startOfDay(parsedDate).toISOString(),
            },
          };
        }
      }

      // Cleans query parameters from the URL after being processed
      history.replace({
        search: '',
      });
    } else {
      this.state = {
        ...defaultState,
      };
    }
  }

  public ModalConfirmDelete = (deleteSchedule: DeleteScheduleMutationFn, queryResult: SchedulesResult) => {
    const { t } = this.props;
    const { isDeleteModalOpen, selectedSchedule } = this.state;

    return (
      isDeleteModalOpen &&
      selectedSchedule && (
        <ModalConfirmation
          headerText={t('schedule.delete')}
          bodyText={t('schedule.modal.deleteScheduleBody', {
            scheduleName: selectedSchedule.name,
          })}
          submitText={t('layout.action.delete')}
          closeText={t('layout.action.cancel')}
          onSubmit={() => this.handleDeleteSchedule(deleteSchedule, selectedSchedule, queryResult.refetch)}
          onClose={() => this.toggleDeleteModal()}
          show={isDeleteModalOpen}
        />
      )
    );
  };

  public setSidebarFilter = (
    filterValues: Parameters<React.ComponentProps<typeof SchedulingStudioFilterSidebar>['setSidebarFilter']>[0]
  ) => {
    const selectedQueueIds = Object.keys(filterValues.queues);

    this.setState((state) => ({
      ...state,
      getSchedulesVariables: {
        ...state.getSchedulesVariables,
        queueIds: selectedQueueIds.toString(),
      },
    }));
  };

  public getSidebarFilterValues = (queryResult: SchedulesResult) => {
    const {
      getSchedulesVariables: { queueIds },
    } = this.state;

    const selectedQueueIds = queueIds?.split(',') ?? [];

    return {
      queues: recordBy(
        (queryResult.data?.queues ?? [])
          .filter(({ id }) => selectedQueueIds.includes(id))
          .map(({ id, name }) => ({
            id,
            name,
            assigned: true,
          })),
        'id'
      ),
    };
  };

  public render() {
    const { t } = this.props;
    const {
      mutationInProgress,
      preOpenedFilterItemIds,
      getSchedulesVariables: { scheduleName, queueIds },
      preFilteredDate,
    } = this.state;

    return (
      <SidebarConsumer>
        {({ isSidebarOpen, toggleSidebar, closeSidebar, openSidebar }) => (
          <BackgroundTasksStatusConsumer>
            {({ autoScheduler: { schedulesStates } }) => (
              <GetSchedulesComponent variables={this.state.getSchedulesVariables} fetchPolicy="network-only">
                {(queryResult: SchedulesResult) => (
                  <DeleteScheduleComponent>
                    {(deleteSchedule) => {
                      const sidebarFilterValues = this.getSidebarFilterValues(queryResult);

                      useEffect(() => {
                        const { schedules: originalSchedules, queues } = queryResult.data ?? {};
                        const schedules = cloneDeep(originalSchedules);

                        schedules?.forEach(({ id }, index) => {
                          if (schedulesStates[id]) {
                            schedules[index].lockType = isScheduleJobRunning(schedulesStates[id].state)
                              ? LockType.Write
                              : LockType.None;
                          }
                        });

                        if (!equals(schedules, originalSchedules)) {
                          queryResult.client.cache.writeQuery({
                            query: GetSchedulesDocument,
                            variables: this.state.getSchedulesVariables,
                            data: {
                              schedules,
                              queues,
                            },
                          });
                        }
                      }, [schedulesStates]);

                      return (
                        <Content
                          header={this.headerProps(t, isSidebarOpen, toggleSidebar, preFilteredDate)}
                          sidebarContent={
                            <SchedulingStudioFilterSidebar
                              handleCancel={closeSidebar}
                              preOpenedItemIds={preOpenedFilterItemIds}
                              queuesResult={queryResult as unknown as QueuesResult} // `queryResult` contains both `schedules` and `queues` data
                              setPreOpenedItemIds={(ids) => this.setState({ preOpenedFilterItemIds: ids })}
                              setSidebarFilter={this.setSidebarFilter}
                              sidebarFilter={sidebarFilterValues}
                            />
                          }
                        >
                          <Container
                            className="scheduling-studio__container"
                            data-test-id="scheduling-studio:container"
                          >
                            <LoadingOverlay loading={queryResult.loading || mutationInProgress}>
                              <ActiveFilters
                                config={[
                                  {
                                    dataTestId: 'scheduling-studio-active-sidebar-filter-queues',
                                    defaultValue: {},
                                    id: '1',
                                    key: 'queues',
                                    label: t('queue.queues'),
                                  },
                                ]}
                                cancelFilterSidebar={() => this.setState({ preOpenedFilterItemIds: [] }, closeSidebar)}
                                openFilterSidebarItem={(itemId) =>
                                  this.setState({ preOpenedFilterItemIds: [itemId] }, openSidebar)
                                }
                                setSidebarFilter={this.setSidebarFilter}
                                sidebarFilter={sidebarFilterValues}
                                isFixed={!queryResult.loading && queryResult.data?.schedules?.length === 0}
                              />
                              {queryResult?.data?.schedules?.length ? (
                                <>
                                  <ScheduleList
                                    t={t}
                                    schedules={this.normalizeSchedules(queryResult)}
                                    handleDeleteSchedule={this.toggleDeleteModal}
                                  />
                                  {this.ModalConfirmDelete(deleteSchedule, queryResult)}
                                </>
                              ) : queryResult.loading || queryResult.error ? null : (
                                <FullSizeMessage
                                  headline={
                                    queueIds || scheduleName ? t('filter.noResultsMatch') : t('schedule.noEntries')
                                  }
                                  iconKind="no_data"
                                />
                              )}
                            </LoadingOverlay>
                          </Container>
                        </Content>
                      );
                    }}
                  </DeleteScheduleComponent>
                )}
              </GetSchedulesComponent>
            )}
          </BackgroundTasksStatusConsumer>
        )}
      </SidebarConsumer>
    );
  }

  public normalizeSchedules = (qr: SchedulesResult): ScheduleType[] => {
    if (!qr.data) {
      return [];
    }

    const { schedules = [], queues = [] } = qr.data;

    if (!schedules || !schedules.length || !queues || !queues.length) {
      return [];
    }

    const normalized: ScheduleType[] = [];

    // Should not be possible to have schedule without queue. But may happen during development.
    const nonExistingQueue = { id: 0, name: '' };
    const queuesRecord = queues.reduce((queuesData, queue) => {
      queuesData[queue.id] = queue;

      return queuesData;
    }, {} as Record<string, Pick<Queue, 'id' | 'name'>>);

    schedules.forEach((schedule) => {
      const norm: ScheduleType = { ...schedule };

      if (schedule.queueIds) {
        const normQueues = schedule.queueIds.map((queueId) => queuesRecord[queueId] || nonExistingQueue);

        if (normQueues.length > 0) {
          norm.queues = normQueues;
        }
      }

      normalized.push(norm);
    });

    return normalized;
  };

  public toggleDeleteModal = (schedule?: ScheduleType) => {
    this.setState({ isDeleteModalOpen: !!schedule, selectedSchedule: schedule });
  };

  protected onFilterSubmit = (props: ListFilterResults) => {
    const { search = '', startDateTime = '', endDateTime = '' } = props;

    const update: GetSchedulesQueryVariables = {
      scheduleName: search.trim(),
      scheduleStartDateTime: startDateTime,
      scheduleEndDateTime: endDateTime ? createEndOfDateRange(endDateTime).toISOString() : endDateTime,
    };
    this.setState((state) => ({ ...state, getSchedulesVariables: { ...state.getSchedulesVariables, ...update } }));
  };

  protected handleDeleteSchedule = async (
    deleteSchedule: DeleteScheduleMutationFn,
    schedule: ScheduleType,
    refetch: () => void
  ) => {
    this.setState({ mutationInProgress: true });
    try {
      await deleteSchedule({
        variables: {
          id: schedule.id,
        },
        refetchQueries: [
          {
            query: GetSchedulesDocument,
            variables: this.state.getSchedulesVariables,
          },
        ],
      });
    } catch (error) {
      // `customFetch()` will display error message
      const errorCode = pathOr(null, ['networkError', 'result', 'errorCode'], error);

      if (errorCode === 'fe.sch.delete.inProd') {
        // refetch data to see that schedule has been put into production in meantime
        refetch();
      }
    } finally {
      this.toggleDeleteModal();
      this.setState({ mutationInProgress: false });
    }
  };

  protected headerProps = (
    t: TFunction,
    isSidebarOpen: boolean,
    toggleSidebar: () => void,
    preFilteredDate?: Date
  ): HeaderPropsType => ({
    actionBar: (
      <Row>
        <ListFilter
          t={t}
          onSubmit={this.onFilterSubmit}
          createButtonPath={PATH_SCHEDULING_STUDIO_CREATE}
          createButtonLabel="schedule.newSchedule"
          requiredPermissionsForCreate={[Permissions.CreateSchedule]}
          preFilteredDate={preFilteredDate}
        />
        <div className="ml-auto">
          <Button
            active={isSidebarOpen}
            className="button--icon--left-hand-side ml-auto"
            color="info"
            data-test-id={TOGGLE_FILTER_BUTTON_TAG}
            onClick={() => this.setState({ preOpenedFilterItemIds: [] }, toggleSidebar)}
          >
            <Icon color="#239ed8" kind="bars" size={20} />
            {t('filter.filters')}
          </Button>
        </div>
      </Row>
    ),
    testId: STUDIO_TITLE_TAG,
    title: t('schedule.schedulingStudio'),
    titleSlug: 'scheduling-studio',
  });
}

export const SchedulingStudioScene = compose(withRouter, withTranslation())(SchedulingStudioSceneComponent);
